<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import TripLayout from '../../components/layout/TripLayout.svelte';
  import { Plane, Plus, Edit2, Trash2, Clock, MapPin, Calendar, AlertCircle } from 'lucide-svelte';

  let { trip, flights = [] }: {
    trip: any;
    flights?: any[];
  } = $props();

  let showModal = $state(false);
  let editingFlight: any = $state(null);
  let isLoading = $state(false);
  let notification: { type: 'success' | 'error'; message: string } | null = $state(null);

  // Form state
  let formData = $state({
    airline: '',
    flight_number: '',
    direction: 'outbound',
    departure_airport: '',
    departure_airport_name: '',
    arrival_airport: '',
    arrival_airport_name: '',
    departure_time: '',
    arrival_time: '',
    departure_terminal: '',
    departure_gate: '',
    arrival_terminal: '',
    confirmation_number: '',
    seat_number: '',
    booking_url: '',
    notes: '',
    boarding_pass_url: ''
  });

  // ========== 탑승권 업로드 (더미 구현) ==========
  let uploadingBoardingPass = $state(false);
  let boardingPassPreview: string | null = $state(null);

  interface UploadResult {
    success: boolean;
    url?: string;
    error?: string;
  }

  async function uploadBoardingPass(file: File): Promise<UploadResult> {
    // 더미 S3 업로드 구현
    // 실제 구현 시: AWS SDK 또는 presigned URL을 사용하여 업로드
    return new Promise((resolve) => {
      setTimeout(() => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            success: true,
            url: e.target?.result as string,
            // 실제 S3 URL 예시: `https://triphelper-bucket.s3.ap-northeast-2.amazonaws.com/boarding-passes/${file.name}`
          });
        };
        reader.onerror = () => {
          resolve({ success: false, error: "파일 읽기 실패" });
        };
        reader.readAsDataURL(file);
      }, 500);
    });
  }

  async function handleBoardingPassUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // 파일 크기 검증 (10MB 제한)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('error', '파일 크기는 10MB 이하여야 합니다');
      return;
    }

    // 파일 타입 검증 (이미지 또는 PDF)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('error', '이미지(JPG, PNG) 또는 PDF 파일만 업로드 가능합니다');
      return;
    }

    uploadingBoardingPass = true;
    try {
      const result = await uploadBoardingPass(file);
      if (result.success && result.url) {
        formData.boarding_pass_url = result.url;
        boardingPassPreview = result.url;
        showNotification('success', '탑승권이 업로드되었습니다');
      } else {
        showNotification('error', result.error || '업로드에 실패했습니다');
      }
    } catch {
      showNotification('error', '업로드 중 오류가 발생했습니다');
    } finally {
      uploadingBoardingPass = false;
    }
  }

  function removeBoardingPass() {
    formData.boarding_pass_url = '';
    boardingPassPreview = null;
  }

  const airlines = [
    { code: 'KE', name: '대한항공' },
    { code: 'OZ', name: '아시아나항공' },
    { code: 'LJ', name: '진에어' },
    { code: 'TW', name: '티웨이항공' },
    { code: '7C', name: '제주항공' },
    { code: 'BX', name: '에어부산' },
    { code: 'RS', name: '에어서울' },
    { code: 'ZE', name: '이스타항공' },
    { code: 'AA', name: 'American Airlines' },
    { code: 'UA', name: 'United Airlines' },
    { code: 'DL', name: 'Delta Air Lines' },
    { code: 'JL', name: 'Japan Airlines' },
    { code: 'NH', name: 'ANA' },
    { code: 'CX', name: 'Cathay Pacific' },
    { code: 'SQ', name: 'Singapore Airlines' },
    { code: 'TG', name: 'Thai Airways' },
    { code: 'EK', name: 'Emirates' },
    { code: 'QR', name: 'Qatar Airways' },
  ];

  function resetForm() {
    formData = {
      airline: '',
      flight_number: '',
      direction: 'outbound',
      departure_airport: '',
      departure_airport_name: '',
      arrival_airport: '',
      arrival_airport_name: '',
      departure_time: '',
      arrival_time: '',
      departure_terminal: '',
      departure_gate: '',
      arrival_terminal: '',
      confirmation_number: '',
      seat_number: '',
      booking_url: '',
      notes: ''
      ,
      boarding_pass_url: ''
    };
    editingFlight = null;
  }

  function openCreateModal() {
    resetForm();
    showModal = true;
  }

  function openEditModal(flight: any) {
    editingFlight = flight;
    formData = {
      airline: flight.airline || '',
      flight_number: flight.flight_number || '',
      direction: flight.direction || 'outbound',
      departure_airport: flight.departure_airport || '',
      departure_airport_name: flight.departure_airport_name || '',
      arrival_airport: flight.arrival_airport || '',
      arrival_airport_name: flight.arrival_airport_name || '',
      departure_time: flight.departure_time ? flight.departure_time.slice(0, 16) : '',
      arrival_time: flight.arrival_time ? flight.arrival_time.slice(0, 16) : '',
      departure_terminal: flight.departure_terminal || '',
      departure_gate: flight.departure_gate || '',
      arrival_terminal: flight.arrival_terminal || '',
      confirmation_number: flight.confirmation_number || '',
      seat_number: flight.seat_number || '',
      booking_url: flight.booking_url || '',
      notes: flight.notes || '',
      boarding_pass_url: flight.boarding_pass_url || ''
    };
    showModal = true;
  }

  function closeModal() {
    showModal = false;
    resetForm();
  }

  async function saveFlight() {
    if (!formData.flight_number || !formData.departure_time) {
      showNotification('error', '편명과 출발 시간은 필수입니다');
      return;
    }

    isLoading = true;
    const url = editingFlight
      ? `/api/v1/trips/${trip.id}/flights/${editingFlight.id}`
      : `/api/v1/trips/${trip.id}/flights`;
    const method = editingFlight ? 'PATCH' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ flight: formData })
      });

      if (response.ok) {
        showNotification('success', editingFlight ? '항공편이 수정되었습니다' : '항공편이 추가되었습니다');
        closeModal();
        router.reload();
      } else {
        const data = await response.json();
        showNotification('error', data.error || '저장에 실패했습니다');
      }
    } catch (e) {
      showNotification('error', '네트워크 오류가 발생했습니다');
    } finally {
      isLoading = false;
    }
  }

  async function deleteFlight(flight: any) {
    if (!confirm(`${flight.flight_number} 항공편을 삭제하시겠습니까?`)) return;

    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/flights/${flight.id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      if (response.ok) {
        showNotification('success', '항공편이 삭제되었습니다');
        router.reload();
      }
    } catch (e) {
      showNotification('error', '삭제에 실패했습니다');
    }
  }

  function showNotification(type: 'success' | 'error', message: string) {
    notification = { type, message };
    setTimeout(() => notification = null, 3000);
  }

  function formatDateTime(dateStr: string) {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getStatusBadge(status: string) {
    const styles: Record<string, string> = {
      scheduled: 'bg-blue-100 text-blue-700',
      delayed: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-green-100 text-green-700'
    };
    const labels: Record<string, string> = {
      scheduled: '예정',
      delayed: '지연',
      cancelled: '취소',
      completed: '완료'
    };
    return { style: styles[status] || styles.scheduled, label: labels[status] || '예정' };
  }

  let outboundFlights = $derived(flights.filter(f => f.direction === 'outbound'));
  let returnFlights = $derived(flights.filter(f => f.direction === 'return'));
</script>

<TripLayout {trip} currentPage="flights">
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-primary">항공편</h1>
        <p class="text-muted mt-1">여행 항공편 정보를 관리하세요</p>
      </div>
      <button
        onclick={openCreateModal}
        class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
      >
        <Plus size={20} />
        항공편 추가
      </button>
    </div>

    <!-- Notification -->
    {#if notification}
      <div class="mb-4 p-4 rounded-lg {notification.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}">
        {notification.message}
      </div>
    {/if}

    {#if flights.length === 0}
      <!-- Empty State -->
      <div class="text-center py-16 bg-surface rounded-xl border border-bright">
        <div class="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full bg-blue-50">
          <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-primary mb-2">등록된 항공편이 없습니다</h3>
        <p class="text-muted mb-6">여행 항공편 정보를 추가해보세요</p>
        <button
          onclick={openCreateModal}
          class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          항공편 추가
        </button>
      </div>
    {:else}
      <!-- Outbound Flights -->
      {#if outboundFlights.length > 0}
        <div class="mb-8">
          <h2 class="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <span class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </span>
            가는 편
          </h2>
          <div class="space-y-4">
            {#each outboundFlights as flight}
              <div class="bg-surface rounded-xl border border-bright p-6 hover:shadow-md transition">
                <div class="flex items-start justify-between">
                  <div class="flex items-center gap-4">
                    <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Plane size={24} class="text-blue-600" />
                    </div>
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="font-bold text-lg">{flight.airline} {flight.flight_number}</span>
                        <span class="px-2 py-0.5 text-xs rounded-full {getStatusBadge(flight.status).style}">{getStatusBadge(flight.status).label}</span>
                      </div>
                      {#if flight.confirmation_number}
                        <p class="text-sm text-muted">예약번호: {flight.confirmation_number}</p>
                      {/if}
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button
                      aria-label="항공편 수정"
                      onclick={() => openEditModal(flight)}
                      class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      aria-label="항공편 삭제"
                      onclick={() => deleteFlight(flight)}
                      class="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div class="mt-4 flex items-center gap-8">
                  <div class="text-center">
                    <p class="text-2xl font-bold">{flight.departure_airport}</p>
                    <p class="text-sm text-muted">{flight.departure_airport_name || ''}</p>
                    <p class="text-sm font-medium mt-1">{formatDateTime(flight.departure_time)}</p>
                    {#if flight.departure_terminal}
                      <p class="text-xs text-muted">터미널 {flight.departure_terminal}</p>
                    {/if}
                  </div>
                  <div class="flex-1 flex items-center">
                    <div class="flex-1 border-t-2 border-dashed border-bright"></div>
                    <Plane size={20} class="mx-2 text-muted" />
                    <div class="flex-1 border-t-2 border-dashed border-bright"></div>
                  </div>
                  <div class="text-center">
                    <p class="text-2xl font-bold">{flight.arrival_airport}</p>
                    <p class="text-sm text-muted">{flight.arrival_airport_name || ''}</p>
                    <p class="text-sm font-medium mt-1">{formatDateTime(flight.arrival_time)}</p>
                    {#if flight.arrival_terminal}
                      <p class="text-xs text-muted">터미널 {flight.arrival_terminal}</p>
                    {/if}
                  </div>
                </div>

                {#if flight.seat_number || flight.notes}
                  <div class="mt-4 pt-4 border-t border-default flex gap-4 text-sm text-secondary">
                    {#if flight.seat_number}
                      <span>좌석: {flight.seat_number}</span>
                    {/if}
                    {#if flight.notes}
                      <span class="text-muted">|</span>
                      <span>{flight.notes}</span>
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Return Flights -->
      {#if returnFlights.length > 0}
        <div>
          <h2 class="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <span class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-purple-600 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </span>
            오는 편
          </h2>
          <div class="space-y-4">
            {#each returnFlights as flight}
              <div class="bg-surface rounded-xl border border-bright p-6 hover:shadow-md transition">
                <div class="flex items-start justify-between">
                  <div class="flex items-center gap-4">
                    <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Plane size={24} class="text-purple-600 rotate-180" />
                    </div>
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="font-bold text-lg">{flight.airline} {flight.flight_number}</span>
                        <span class="px-2 py-0.5 text-xs rounded-full {getStatusBadge(flight.status).style}">{getStatusBadge(flight.status).label}</span>
                      </div>
                      {#if flight.confirmation_number}
                        <p class="text-sm text-muted">예약번호: {flight.confirmation_number}</p>
                      {/if}
                    </div>
                  </div>
                  <div class="flex gap-2">
                    <button
                      aria-label="항공편 수정"
                      onclick={() => openEditModal(flight)}
                      class="p-2 text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      aria-label="항공편 삭제"
                      onclick={() => deleteFlight(flight)}
                      class="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <div class="mt-4 flex items-center gap-8">
                  <div class="text-center">
                    <p class="text-2xl font-bold">{flight.departure_airport}</p>
                    <p class="text-sm text-muted">{flight.departure_airport_name || ''}</p>
                    <p class="text-sm font-medium mt-1">{formatDateTime(flight.departure_time)}</p>
                  </div>
                  <div class="flex-1 flex items-center">
                    <div class="flex-1 border-t-2 border-dashed border-bright"></div>
                    <Plane size={20} class="mx-2 text-muted" />
                    <div class="flex-1 border-t-2 border-dashed border-bright"></div>
                  </div>
                  <div class="text-center">
                    <p class="text-2xl font-bold">{flight.arrival_airport}</p>
                    <p class="text-sm text-muted">{flight.arrival_airport_name || ''}</p>
                    <p class="text-sm font-medium mt-1">{formatDateTime(flight.arrival_time)}</p>
                  </div>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Modal -->
  {#if showModal}
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-surface rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-bright">
          <h2 class="text-xl font-bold">{editingFlight ? '항공편 수정' : '항공편 추가'}</h2>
        </div>

        <form onsubmit={(e) => { e.preventDefault(); saveFlight(); }} class="p-6 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="flight-airline" class="block text-sm font-medium text-secondary mb-1">항공사</label>
              <select
                id="flight-airline"
                bind:value={formData.airline}
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">선택</option>
                {#each airlines as airline}
                  <option value={airline.code}>{airline.name} ({airline.code})</option>
                {/each}
              </select>
            </div>
            <div>
              <label for="flight-number" class="block text-sm font-medium text-secondary mb-1">편명 *</label>
              <input
                id="flight-number"
                type="text"
                bind:value={formData.flight_number}
                placeholder="KE123"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-secondary mb-1" for="flight-direction-outbound">구분</label>
            <div class="flex gap-4" role="radiogroup" aria-label="항공편 구분">
              <label class="flex items-center gap-2" for="flight-direction-outbound">
                <input id="flight-direction-outbound" type="radio" bind:group={formData.direction} value="outbound" />
                <span>가는 편</span>
              </label>
              <label class="flex items-center gap-2" for="flight-direction-return">
                <input id="flight-direction-return" type="radio" bind:group={formData.direction} value="return" />
                <span>오는 편</span>
              </label>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="flight-departure-airport" class="block text-sm font-medium text-secondary mb-1">출발 공항 (IATA)</label>
              <input
                id="flight-departure-airport"
                type="text"
                bind:value={formData.departure_airport}
                placeholder="ICN"
                maxlength="3"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
              />
            </div>
            <div>
              <label for="flight-departure-airport-name" class="block text-sm font-medium text-secondary mb-1">출발 공항명</label>
              <input
                id="flight-departure-airport-name"
                type="text"
                bind:value={formData.departure_airport_name}
                placeholder="인천국제공항"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="flight-arrival-airport" class="block text-sm font-medium text-secondary mb-1">도착 공항 (IATA)</label>
              <input
                id="flight-arrival-airport"
                type="text"
                bind:value={formData.arrival_airport}
                placeholder="NRT"
                maxlength="3"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
              />
            </div>
            <div>
              <label for="flight-arrival-airport-name" class="block text-sm font-medium text-secondary mb-1">도착 공항명</label>
              <input
                id="flight-arrival-airport-name"
                type="text"
                bind:value={formData.arrival_airport_name}
                placeholder="나리타국제공항"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="flight-departure-time" class="block text-sm font-medium text-secondary mb-1">출발 시간 *</label>
              <input
                id="flight-departure-time"
                type="datetime-local"
                bind:value={formData.departure_time}
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label for="flight-arrival-time" class="block text-sm font-medium text-secondary mb-1">도착 시간</label>
              <input
                id="flight-arrival-time"
                type="datetime-local"
                bind:value={formData.arrival_time}
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <div>
              <label for="flight-departure-terminal" class="block text-sm font-medium text-secondary mb-1">출발 터미널</label>
              <input
                id="flight-departure-terminal"
                type="text"
                bind:value={formData.departure_terminal}
                placeholder="T1"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label for="flight-departure-gate" class="block text-sm font-medium text-secondary mb-1">게이트</label>
              <input
                id="flight-departure-gate"
                type="text"
                bind:value={formData.departure_gate}
                placeholder="A12"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label for="flight-arrival-terminal" class="block text-sm font-medium text-secondary mb-1">도착 터미널</label>
              <input
                id="flight-arrival-terminal"
                type="text"
                bind:value={formData.arrival_terminal}
                placeholder="T2"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label for="flight-confirmation-number" class="block text-sm font-medium text-secondary mb-1">예약 확인번호</label>
              <input
                id="flight-confirmation-number"
                type="text"
                bind:value={formData.confirmation_number}
                placeholder="ABC123"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label for="flight-seat-number" class="block text-sm font-medium text-secondary mb-1">좌석 번호</label>
              <input
                id="flight-seat-number"
                type="text"
                bind:value={formData.seat_number}
                placeholder="12A"
                class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label for="flight-notes" class="block text-sm font-medium text-secondary mb-1">메모</label>
            <textarea
              id="flight-notes"
              bind:value={formData.notes}
              rows="2"
              placeholder="추가 메모"
              class="w-full px-3 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>

          <!-- 탑승권 업로드 -->
          <div>
            <label for="flight-boarding-pass" class="block text-sm font-medium text-secondary mb-1">탑승권 (선택)</label>
            <div class="border-2 border-dashed border-bright rounded-lg p-4">
              {#if boardingPassPreview || formData.boarding_pass_url}
                <div class="relative">
                  {#if (boardingPassPreview || formData.boarding_pass_url)?.includes('application/pdf') || (boardingPassPreview || formData.boarding_pass_url)?.endsWith('.pdf')}
                    <div class="flex items-center gap-3 p-3 bg-app rounded-lg">
                      <svg class="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span class="text-sm text-secondary">탑승권 PDF</span>
                    </div>
                  {:else}
                    <img
                      src={boardingPassPreview || formData.boarding_pass_url}
                      alt="탑승권"
                      class="max-h-40 rounded-lg mx-auto"
                    />
                  {/if}
                  <button
                    type="button"
                    aria-label="탑승권 제거"
                    onclick={removeBoardingPass}
                    class="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              {:else}
                <label class="flex flex-col items-center cursor-pointer">
                  {#if uploadingBoardingPass}
                    <svg class="animate-spin w-8 h-8 text-muted mb-2" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span class="text-sm text-muted">업로드 중...</span>
                  {:else}
                    <svg class="w-8 h-8 text-muted mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span class="text-sm text-muted">클릭하여 탑승권 업로드</span>
                    <span class="text-xs text-muted mt-1">JPG, PNG, PDF (최대 10MB)</span>
                  {/if}
                  <input
                    id="flight-boarding-pass"
                    type="file"
                    accept="image/*,.pdf"
                    class="hidden"
                    onchange={handleBoardingPassUpload}
                    disabled={uploadingBoardingPass}
                  />
                </label>
              {/if}
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onclick={closeModal}
              class="px-4 py-2 text-secondary bg-surface-hover rounded-lg hover:bg-surface-hover"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  {/if}
</TripLayout>
